import { ref, onMounted, onUnmounted } from 'vue';

export function useDeviceOrientation() {
  const isFaceDown = ref(false);
  const alpha = ref<number | null>(null);
  const beta = ref<number | null>(null);
  const gamma = ref<number | null>(null);
  const isSupported = ref(false);
  const permissionGranted = ref(false);

  /**
   * Prüft, ob das Gerät mit dem Display nach unten liegt
   * Beta-Wert nahe 180° bedeutet: Display nach unten
   */
  function checkFaceDown(betaValue: number | null) {
    if (betaValue === null) return;

    // Beta: -180 bis 180
    // Display nach unten: ca. 150° bis 210° (oder -180° bis -150°)
    const threshold = 30; // Toleranz in Grad
    const faceDownAngle = 180;

    const diff = Math.abs(Math.abs(betaValue) - faceDownAngle);
    isFaceDown.value = diff < threshold;
  }

  /**
   * Event-Handler für DeviceOrientation
   */
  function handleOrientation(event: DeviceOrientationEvent) {
    alpha.value = event.alpha;
    beta.value = event.beta;
    gamma.value = event.gamma;

    checkFaceDown(beta.value);
  }

  /**
   * Fordert Berechtigung an (iOS 13+)
   */
  async function requestPermission(): Promise<boolean> {
    if (typeof DeviceOrientationEvent === 'undefined') {
      console.warn('DeviceOrientation wird nicht unterstützt');
      return false;
    }

    // iOS 13+ erfordert explizite Berechtigung
    if (
      typeof (DeviceOrientationEvent as any).requestPermission === 'function'
    ) {
      try {
        const permission = await (DeviceOrientationEvent as any).requestPermission();
        permissionGranted.value = permission === 'granted';
        return permissionGranted.value;
      } catch (error) {
        console.error('Fehler beim Anfordern der Berechtigung:', error);
        return false;
      }
    } else {
      // Andere Browser: keine Berechtigung nötig
      permissionGranted.value = true;
      return true;
    }
  }

  /**
   * Startet das Listening
   */
  async function startListening() {
    const granted = await requestPermission();
    
    if (granted) {
      window.addEventListener('deviceorientation', handleOrientation);
      isSupported.value = true;
    }
  }

  /**
   * Stoppt das Listening
   */
  function stopListening() {
    window.removeEventListener('deviceorientation', handleOrientation);
  }

  // Bei Mount: Automatisch starten
  onMounted(() => {
    startListening();
  });

  // Bei Unmount: Cleanup
  onUnmounted(() => {
    stopListening();
  });

  return {
    isFaceDown,
    alpha,
    beta,
    gamma,
    isSupported,
    permissionGranted,
    requestPermission,
    startListening,
    stopListening
  };
}